import type { Stats, DetailedStats, FormStats, LearningInsight } from "@/types";

/**
 * UI Elements interface for stats dashboard
 */
interface StatsDashboardElements {
  statsContent: HTMLElement;
  detailedStats: HTMLElement;
}

/**
 * Enhanced stats tracker interface with analytics capabilities
 */
interface StatsTracker {
  getDetailedStats(): DetailedStats;
  getStats(): Stats;
  getFormStats(tense: string, pronoun: string): FormStats;
  getLearningInsights(): LearningInsight[];
}

/**
 * Gamification system interface
 */
interface GamificationSystem {
  streaks: {
    current: number;
  };
  getLevel(): number;
}

/**
 * Achievement system interface (placeholder)
 */
interface AchievementSystem {
  // Add methods as needed
}

/**
 * Simple statistics dashboard component
 */
export class StatsDashboard {
  private elements: StatsDashboardElements;
  private statsTracker: StatsTracker;
  private gamificationSystem: GamificationSystem;

  constructor(
    elements: StatsDashboardElements,
    statsTracker: StatsTracker,
    gamificationSystem: GamificationSystem,
    _achievementSystem: AchievementSystem
  ) {
    this.elements = elements;
    this.statsTracker = statsTracker;
    this.gamificationSystem = gamificationSystem;

    // Listen for show stats event
    const eventManager = (
      window as unknown as {
        eventManager?: { on(event: string, callback: () => void): void };
      }
    ).eventManager;
    eventManager?.on("ui:show-detailed-stats", () => this.show());
  }

  /**
   * Show detailed statistics with enhanced analytics
   */
  show(): void {
    const detailedStats = this.statsTracker.getDetailedStats();
    const overallStats = this.statsTracker.getStats();

    // Get learning insights if available
    let insights: LearningInsight[] = [];
    try {
      insights = this.statsTracker.getLearningInsights();

      // Debug: Add sample insights if none available (remove in production)
      if (
        insights.length === 0 &&
        window.location.search.includes("debug=insights")
      ) {
        insights = this.generateSampleInsights();
      }
    } catch {
      // Gracefully handle if analytics features aren't available
    }

    let html = this.generateOverviewSection(overallStats);

    // Add analytics sections if insights are available
    if (insights.length > 0) {
      html += this.generateInsightsSection(insights);
    }

    html += this.generateDetailedBreakdown(detailedStats);

    if (html === "") {
      html = this.generateEmptyState();
    }

    this.elements.statsContent.innerHTML = html;
    this.elements.detailedStats.style.display = "block";
  }

  /**
   * Generate overview cards section
   */
  private generateOverviewSection(overallStats: Stats): string {
    return `
      <div class="stats-overview">
        <div class="overview-cards">
          <div class="overview-card">
            <div class="card-icon">📊</div>
            <div class="card-value">${overallStats.accuracy}%</div>
            <div class="card-label">Overall Accuracy</div>
          </div>
          <div class="overview-card">
            <div class="card-icon">💪</div>
            <div class="card-value">${overallStats.total}</div>
            <div class="card-label">Total Attempts</div>
          </div>
          <div class="overview-card">
            <div class="card-icon">🔥</div>
            <div class="card-value">${
              this.gamificationSystem.streaks.current
            }</div>
            <div class="card-label">Current Streak</div>
          </div>
          <div class="overview-card">
            <div class="card-icon">🏆</div>
            <div class="card-value">${this.gamificationSystem.getLevel()}</div>
            <div class="card-label">Current Level</div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Generate learning insights section
   */
  private generateInsightsSection(insights: LearningInsight[]): string {
    if (insights.length === 0) return "";

    let html = `
      <div class="insights-section">
        <h4>🎯 Learning Insights</h4>
        <div class="insights-grid">
    `;

    for (const insight of insights.slice(0, 4)) {
      // Show top 4 insight categories
      html += `
        <div class="insight-category ${insight.type}">
          <div class="insight-icon">${this.getInsightIcon(insight.type)}</div>
          <div class="insight-content">
            <div class="insight-title">${insight.title}</div>
            <div class="insight-items">
              ${insight.items
                .slice(0, 3)
                .map(
                  (item) => `
                <div class="insight-item ${item.priority}">
                  <span class="form-name">${item.form}</span>
                  <span class="form-reason">${item.reason}</span>
                </div>
              `
                )
                .join("")}
              ${
                insight.items.length > 3
                  ? `<div class="more-items">+${
                      insight.items.length - 3
                    } more</div>`
                  : ""
              }
            </div>
          </div>
        </div>
      `;
    }

    html += `
        </div>
      </div>
    `;

    return html;
  }

  /**
   * Generate detailed breakdown section
   */
  private generateDetailedBreakdown(detailedStats: DetailedStats): string {
    let html = `<div class="detailed-breakdown"><h4>📚 Practice by Tense</h4>`;

    for (const [tense, pronounStats] of Object.entries(detailedStats)) {
      const tenseName =
        (
          window as unknown as {
            verbData?: { getTenseName(tense: string): string };
          }
        ).verbData?.getTenseName(tense) || tense;
      const tenseStats = Object.values(pronounStats);
      const tenseAccuracy =
        tenseStats.length > 0
          ? Math.round(
              tenseStats.reduce((sum, s) => sum + s.accuracy, 0) /
                tenseStats.length
            )
          : 0;
      const tensePracticed = tenseStats.filter((s) => s.total > 0).length;
      const totalPossiblePronouns = 6; // ich, du, er/sie/es, wir, ihr, sie/Sie

      // Calculate proficiency level
      const proficiencyLevel = this.calculateProficiencyLevel(tenseStats);

      html += `
        <div class="tense-section">
          <div class="tense-header">
            <h5>${tenseName}</h5>
            <div class="tense-summary">
              <span class="tense-accuracy ${this.getAccuracyClass(
                tenseAccuracy
              )}">${tenseAccuracy}% avg</span>
              <span class="tense-progress">${tensePracticed}/${totalPossiblePronouns} practiced</span>
              <span class="proficiency-badge ${proficiencyLevel.toLowerCase()}">${proficiencyLevel}</span>
            </div>
          </div>

          <div class="pronoun-grid">
            ${Object.entries(pronounStats)
              .map(([pronoun, stats]) =>
                this.generatePronounCard(pronoun, stats)
              )
              .join("")}
          </div>
        </div>
      `;
    }

    html += `</div>`;
    return html;
  }

  /**
   * Generate individual pronoun performance card
   */
  private generatePronounCard(pronoun: string, stats: FormStats): string {
    const hasData = stats.total > 0;
    const accuracyClass = this.getAccuracyClass(stats.accuracy);
    const timeWeightedAccuracy = stats.timeWeightedAccuracy || stats.accuracy;

    return `
      <div class="pronoun-card ${hasData ? "has-data" : "no-data"}">
        <div class="pronoun-name">${pronoun}</div>
        <div class="pronoun-stats">
          ${
            hasData
              ? `
            <div class="accuracy ${accuracyClass}">${stats.accuracy}%</div>
            <div class="attempts">${stats.correct}/${stats.total}</div>
            ${
              timeWeightedAccuracy !== stats.accuracy
                ? `<div class="trend ${
                    timeWeightedAccuracy > stats.accuracy
                      ? "improving"
                      : "declining"
                  }">
                ${
                  timeWeightedAccuracy > stats.accuracy ? "↗" : "↘"
                } ${timeWeightedAccuracy}%
              </div>`
                : ""
            }
          `
              : `
            <div class="not-practiced">Not practiced</div>
          `
          }
        </div>
      </div>
    `;
  }

  /**
   * Generate empty state
   */
  private generateEmptyState(): string {
    return `
      <div class="no-stats">
        <div class="no-stats-icon">📚</div>
        <h4>Ready to start learning?</h4>
        <p>No statistics available yet. Start practicing to see your progress!</p>
      </div>
    `;
  }

  /**
   * Get insight icon based on type
   */
  private getInsightIcon(type: string): string {
    const icons: { [key: string]: string } = {
      struggling: "⚠️",
      improving: "📈",
      mastered: "✅",
      stale: "⏰",
    };
    return icons[type] || "💡";
  }

  /**
   * Debug method to generate sample insights for testing
   * Remove this in production
   */
  private generateSampleInsights(): LearningInsight[] {
    return [
      {
        type: "struggling",
        title: "Forms that need attention",
        items: [
          {
            form: "present - sie/Sie",
            reason: "50% accuracy needs improvement",
            priority: "high" as const,
          },
        ],
      },
      {
        type: "improving",
        title: "Improving forms",
        items: [
          {
            form: "past - ich",
            reason: "Improving trend: 60% → 85%",
            priority: "normal" as const,
          },
        ],
      },
    ];
  }

  /**
   * Get CSS class based on accuracy
   */
  private getAccuracyClass(accuracy: number): string {
    if (accuracy >= 90) return "excellent";
    if (accuracy >= 80) return "good";
    if (accuracy >= 60) return "fair";
    return "needs-work";
  }

  /**
   * Calculate proficiency level for a tense
   */
  private calculateProficiencyLevel(tenseStats: FormStats[]): string {
    const totalPossiblePronouns = 6; // ich, du, er/sie/es, wir, ihr, sie/Sie
    const totalPracticed = tenseStats.filter((s) => s.total > 0).length;
    const avgAccuracy =
      tenseStats.length > 0
        ? tenseStats.reduce((sum, s) => sum + s.accuracy, 0) / tenseStats.length
        : 0;

    // Calculate coverage percentage based on ALL possible pronouns, not just the ones in tenseStats
    const coveragePercentage = totalPracticed / totalPossiblePronouns;

    // For mastery, require ALL pronouns to be practiced with high accuracy
    if (totalPracticed === totalPossiblePronouns && avgAccuracy >= 85) {
      return "Mastered";
    }

    // For proficiency, require at least 70% coverage with good accuracy
    if (coveragePercentage >= 0.7 && avgAccuracy >= 70 && totalPracticed >= 4) {
      return "Proficient";
    }

    // For learning, require at least 50% coverage
    if (coveragePercentage >= 0.5 && totalPracticed >= 3) {
      return "Learning";
    }

    return "Beginner";
  }
}

// Export for global usage
export default StatsDashboard;

// Make available globally for backward compatibility
if (typeof window !== "undefined") {
  (
    window as unknown as { StatsDashboard: typeof StatsDashboard }
  ).StatsDashboard = StatsDashboard;
}
